// "Switch account" dialog opened from the sidebar: the restaurant grid in a modal.
//
// Key export: `SwitchRestaurantDialog`.
// Neighbours: `shell/Sidebar.tsx` opens it; `RestaurantGrid.tsx` is its body;
// `hooks/useOpenRestaurant.ts` does the switch.
//
// Escape and a click on the backdrop close it. A successful switch closes it and
// lands on the new restaurant's landing page.

import { useEffect } from 'react'
import { X } from 'lucide-react'
import { useTeam } from '@/state/useTeam'
import { useOpenRestaurant } from '@/hooks/useOpenRestaurant'
import { RestaurantGrid } from './RestaurantGrid'

export function SwitchRestaurantDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { teams, activeTeamId } = useTeam()
  const { open: openTeam, opening, error } = useOpenRestaurant(onClose)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-16" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Switch account"
        className="max-h-[80vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-border bg-background p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-foreground">
            Switch account <span className="text-sm font-normal text-muted-foreground">({teams.length})</span>
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1 text-muted-foreground hover:bg-accent">
            <X className="h-4 w-4" />
          </button>
        </div>
        {error && (
          <p className="mb-3 rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-2 text-sm text-destructive">{error}</p>
        )}
        <RestaurantGrid teams={teams} activeTeamId={activeTeamId} opening={opening} onPick={openTeam} autoFocusFilter />
      </div>
    </div>
  )
}
